import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Megaphone,
  Plus,
  Trash2,
  Clock,
  AlertTriangle,
  Bell,
  Search,
  Sparkles,
  Radio,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { API } from '../lib/api';
import { cn } from '../lib/utils';
import { ContextualState, PageLoadingState } from '../components/StatePanel';

const priorityFilters = ['all', 'urgent', 'important', 'normal'];

const Announcements = () => {
  const { tournamentId } = useParams();
  const { getAuthHeader, isOrganizer } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    priority: 'normal',
  });

  const fetchData = useCallback(
    async (showError = true) => {
      try {
        const [tournamentRes, announcementsRes] = await Promise.all([
          axios.get(`${API}/tournaments/${tournamentId}`, getAuthHeader()),
          axios.get(`${API}/tournaments/${tournamentId}/announcements`, getAuthHeader()),
        ]);
        setTournament(tournamentRes.data);
        setAnnouncements(announcementsRes.data);
      } catch (error) {
        if (showError) {
          toast.error('Failed to load announcements');
        }
      } finally {
        setLoading(false);
      }
    },
    [tournamentId, getAuthHeader]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useAutoRefresh(() => fetchData(false));

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.message) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/tournaments/${tournamentId}/announcements`,
        formData,
        getAuthHeader()
      );
      setAnnouncements([response.data, ...announcements]);
      setDialogOpen(false);
      setFormData({ title: '', message: '', priority: 'normal' });
      toast.success('Announcement posted!');
    } catch (error) {
      toast.error('Failed to post announcement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;

    try {
      await axios.delete(`${API}/announcements/${id}`, getAuthHeader());
      setAnnouncements(announcements.filter((announcement) => announcement.id !== id));
      toast.success('Announcement deleted');
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const filteredAnnouncements = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return announcements.filter((announcement) => {
      const matchesPriority =
        priorityFilter === 'all' || announcement.priority === priorityFilter;
      const matchesQuery =
        !normalizedQuery ||
        announcement.title?.toLowerCase().includes(normalizedQuery) ||
        announcement.message?.toLowerCase().includes(normalizedQuery) ||
        announcement.created_by?.toLowerCase().includes(normalizedQuery);

      return matchesPriority && matchesQuery;
    });
  }, [announcements, searchQuery, priorityFilter]);

  const summaryCards = useMemo(
    () => [
      {
        label: 'Total Updates',
        value: announcements.length,
        icon: Megaphone,
        tone: 'border-white/10 bg-white/[0.04]',
      },
      {
        label: 'Urgent Alerts',
        value: announcements.filter((announcement) => announcement.priority === 'urgent').length,
        icon: AlertTriangle,
        tone: 'border-rose-500/25 bg-rose-500/[0.05] text-rose-100',
      },
      {
        label: 'Important Notices',
        value: announcements.filter((announcement) => announcement.priority === 'important').length,
        icon: Bell,
        tone: 'border-amber-500/25 bg-amber-500/[0.05] text-amber-100',
      },
      {
        label: 'Live Feed Refresh',
        value: '10s',
        icon: Radio,
        tone: 'border-sky-500/25 bg-sky-500/[0.05] text-sky-100',
      },
    ],
    [announcements]
  );

  const getPriorityMeta = (priority) => {
    switch (priority) {
      case 'urgent':
        return {
          badge: 'bg-rose-500 text-white',
          icon: <AlertTriangle className="h-4 w-4" />,
          card: 'border-rose-500/25 bg-rose-500/[0.04]',
        };
      case 'important':
        return {
          badge: 'bg-amber-500 text-black',
          icon: <Bell className="h-4 w-4" />,
          card: 'border-amber-500/25 bg-amber-500/[0.04]',
        };
      default:
        return {
          badge: 'bg-sky-500 text-white',
          icon: <Megaphone className="h-4 w-4" />,
          card: 'border-white/10 bg-background',
        };
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageLoadingState body="list" />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 md:space-y-8">
        <section className="relative overflow-hidden rounded-[32px] border border-white/10 bg-[#08111f] text-white shadow-[0_24px_80px_rgba(3,8,20,0.45)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.22),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(245,158,11,0.18),transparent_30%)]" />
          <div className="relative p-5 md:p-7">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
              <div className="space-y-4">
                <Link
                  to={`/tournaments/${tournamentId}`}
                  className="inline-flex items-center gap-2 text-sm text-white/70 transition hover:text-white"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Tournament Hub
                </Link>
                <div className="space-y-3">
                  <Badge className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.28em] text-white/80">
                    Announcement Desk
                  </Badge>
                  <div>
                    <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
                      {isOrganizer() ? 'Broadcast tournament updates clearly' : 'Stay on top of every tournament update'}
                    </h1>
                    <p className="mt-2 max-w-3xl text-sm text-white/72 md:text-base">
                      {isOrganizer()
                        ? 'Post match notices, urgent alerts, and operational updates into a communication feed that refreshes automatically for everyone.'
                        : 'Track schedule changes, organizer notices, and high-priority alerts from one live-updating communications board.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:min-w-[360px] xl:max-w-[420px]">
                <div className="rounded-2xl border border-white/10 bg-white/8 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.22em] text-white/60">Tournament</p>
                  <p className="mt-2 text-lg font-semibold">{tournament?.name}</p>
                  <p className="mt-1 text-sm text-white/65">
                    {announcements.length} announcement{announcements.length === 1 ? '' : 's'} in the feed
                  </p>
                </div>
                <div className="rounded-2xl border border-sky-400/20 bg-sky-400/10 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.22em] text-sky-100/70">Feed State</p>
                  <p className="mt-2 text-lg font-semibold text-sky-50">Auto-refresh active</p>
                  <p className="mt-1 text-sm text-sky-50/75">
                    Updates appear automatically every 10 seconds and when the page regains focus.
                  </p>
                </div>
              </div>
            </div>

            {isOrganizer() && (
              <div className="mt-5">
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="h-11 bg-white text-slate-950 hover:bg-white/90"
                      data-testid="create-announcement-button"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      New Announcement
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[560px]">
                    <DialogHeader>
                      <DialogTitle>New Announcement</DialogTitle>
                      <DialogDescription>
                        Post a tournament update for players, fans, and staff.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="mt-4 space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="title">Title *</Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          placeholder="Match schedule update"
                          data-testid="announcement-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          placeholder="Write your announcement here..."
                          rows={5}
                          data-testid="announcement-message-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Select
                          value={formData.priority}
                          onValueChange={(value) => setFormData({ ...formData, priority: value })}
                        >
                          <SelectTrigger data-testid="announcement-priority-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="normal">Normal</SelectItem>
                            <SelectItem value="important">Important</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-3 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setDialogOpen(false)}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="flex-1" data-testid="submit-announcement-button">
                          Post
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.label} className={cn('border-white/10 shadow-sm', card.tone)}>
                <CardContent className="flex items-center justify-between p-5">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {card.label}
                    </p>
                    <p className="mt-2 text-3xl font-semibold tracking-tight">{card.value}</p>
                  </div>
                  <div className="rounded-2xl border border-current/10 bg-background/40 p-3">
                    <Icon className="h-5 w-5" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.35fr_0.65fr]">
          <div className="space-y-6">
            <Card className="border-white/10 shadow-sm">
              <CardContent className="space-y-4 p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      Communication Feed
                    </p>
                    <h2 className="mt-1 text-2xl font-semibold tracking-tight">Latest announcements</h2>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative min-w-[220px] flex-1 sm:flex-none">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search title, message, or author"
                        className="pl-10"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {priorityFilters.map((filter) => (
                        <Button
                          key={filter}
                          type="button"
                          variant={priorityFilter === filter ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPriorityFilter(filter)}
                          className="capitalize"
                        >
                          {filter}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                {filteredAnnouncements.length === 0 ? (
                  <ContextualState
                    icon={Megaphone}
                    eyebrow="Communication Feed"
                    title={announcements.length === 0 ? 'No announcements yet' : 'No announcements match this view'}
                    description={
                      announcements.length === 0
                        ? isOrganizer()
                          ? 'Start the communication stream with the first update for your tournament participants.'
                          : 'The organizer has not posted any updates yet.'
                        : 'Try a different search term or priority filter to bring those updates back into view.'
                    }
                    action={
                      announcements.length === 0 && isOrganizer() ? (
                        <Button onClick={() => setDialogOpen(true)} className="gap-2">
                          <Plus className="h-4 w-4" />
                          Create First Announcement
                        </Button>
                      ) : null
                    }
                  />
                ) : (
                  <div className="space-y-4" data-testid="announcements-list">
                    {filteredAnnouncements.map((announcement, index) => {
                      const meta = getPriorityMeta(announcement.priority);

                      return (
                        <Card
                          key={announcement.id}
                          className={cn(
                            'border-white/10 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md',
                            meta.card
                          )}
                          style={{ animationDelay: `${index * 50}ms` }}
                          data-testid={`announcement-card-${announcement.id}`}
                        >
                          <CardContent className="p-4 md:p-5">
                            <div className="flex items-start justify-between gap-4">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge className={meta.badge}>
                                    {meta.icon}
                                    <span className="ml-1 capitalize">{announcement.priority}</span>
                                  </Badge>
                                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                    <Clock className="h-3.5 w-3.5" />
                                    {new Date(announcement.created_at).toLocaleString()}
                                  </span>
                                </div>

                                <h3 className="mt-3 text-xl font-semibold tracking-tight">
                                  {announcement.title}
                                </h3>
                                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">
                                  {announcement.message}
                                </p>
                                <p className="mt-3 text-xs text-muted-foreground">
                                  Posted by {announcement.created_by}
                                </p>
                              </div>

                              {isOrganizer() && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDelete(announcement.id)}
                                  data-testid={`delete-announcement-${announcement.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-white/10 bg-[#091222] text-white shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="h-5 w-5 text-cyan-300" />
                  Broadcast Insights
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-white/72">
                <div className="rounded-2xl border border-white/10 bg-white/6 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-white/55">Urgency mix</p>
                  <p className="mt-2 text-base font-semibold text-white">
                    {announcements.filter((announcement) => announcement.priority !== 'normal').length} highlighted updates
                  </p>
                  <p className="mt-1">
                    Use priority levels to keep critical notices easy to spot for viewers and participants.
                  </p>
                </div>

                <div className="space-y-3 rounded-2xl border border-white/10 bg-white/6 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-white/55">Feed cues</p>
                  <div className="space-y-3">
                    <InsightRow
                      icon={Radio}
                      title="Auto-refreshing feed"
                      description="Players and viewers receive fresh updates without needing to reload the page manually."
                    />
                    <InsightRow
                      icon={Megaphone}
                      title="Role-aware publishing"
                      description="Only organizers see compose and delete controls, while viewers keep a clean read-only feed."
                    />
                    <InsightRow
                      icon={Bell}
                      title="Priority visibility"
                      description="Urgent and important notices get stronger color and hierarchy so they stand out instantly."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/10 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <QuickLink
                  to={`/tournaments/${tournamentId}`}
                  title="Tournament hub"
                  description="Return to the main control view for this competition."
                />
                <QuickLink
                  to={`/tournaments/${tournamentId}/matches`}
                  title="Fixtures"
                  description="Cross-check schedule changes before posting the next update."
                />
                <QuickLink
                  to={`/tournaments/${tournamentId}/live`}
                  title="Live center"
                  description="Move into live coverage and current match activity."
                />
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </Layout>
  );
};

const InsightRow = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3">
    <div className="rounded-xl border border-white/10 bg-white/8 p-2">
      <Icon className="h-4 w-4 text-white" />
    </div>
    <div>
      <p className="font-medium text-white">{title}</p>
      <p className="mt-1 text-sm text-white/65">{description}</p>
    </div>
  </div>
);

const QuickLink = ({ to, title, description }) => (
  <Link
    to={to}
    className="flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3 transition hover:border-primary/30 hover:bg-primary/[0.04]"
  >
    <div>
      <p className="font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
    <ChevronRight className="h-4 w-4 text-muted-foreground" />
  </Link>
);

export default Announcements;
